import { useEffect, useRef, useState } from 'react';
import { Box, HStack, IconButton, Text, useToast } from '@chakra-ui/react';
import { Download, X } from 'lucide-react';
import { ProgressBar } from '../ProgressBar';
import { formatBytes } from './messageUtils';
import { fetchWithProgress, saveBlob } from '../../lib/download';
import { fetchDecryptedBlob } from '../../lib/e2ee/media';
import type { FileSecret } from '../../lib/e2ee/crypto';
import { useT } from '../../i18n';

interface FileCardProps {
  url: string;
  fileName: string;
  fileSize?: number;
  /** End-to-end encrypted file: decrypted on the device (M28). */
  secret?: FileSecret;
  mimeType?: string;
}

// A file in the chat (design: "Downloading & sending in a chat"): the
// download streams in with a progress bar and can be cancelled, then the
// browser saves it under its real name.
export function FileCard({
  url,
  fileName,
  fileSize,
  secret,
  mimeType,
}: FileCardProps) {
  const toast = useToast();
  const t = useT();
  const [progress, setProgress] = useState<{
    loaded: number;
    total: number;
  } | null>(null);
  const abort = useRef<AbortController | null>(null);
  const extension = fileName.includes('.')
    ? fileName.split('.').pop()!.slice(0, 4).toUpperCase()
    : t('chat.file.badge');

  // Leaving the chat cancels a download in flight.
  useEffect(() => () => abort.current?.abort(), []);

  const download = async () => {
    const controller = new AbortController();
    abort.current = controller;
    setProgress({ loaded: 0, total: fileSize ?? 0 });
    try {
      const options = {
        signal: controller.signal,
        sizeHint: fileSize,
        onProgress: (loaded: number, total: number) =>
          setProgress({ loaded, total }),
      };
      const blob = secret
        ? await fetchDecryptedBlob(url, secret, mimeType, options)
        : await fetchWithProgress(url, options);
      saveBlob(blob, fileName);
    } catch {
      if (!controller.signal.aborted) {
        toast({
          title: t('chat.file.couldNotDownload', { name: fileName }),
          status: 'error',
        });
      }
    } finally {
      abort.current = null;
      setProgress(null);
    }
  };

  return (
    <HStack
      spacing={3}
      p="10px"
      mt={1}
      borderRadius="18px"
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      maxW="360px"
    >
      <Box
        w="44px"
        h="44px"
        borderRadius="12px"
        bg={extension === 'PDF' ? 'accent.tint' : 'surface.subtle'}
        color={extension === 'PDF' ? 'accent' : 'ink.body'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="10px"
        fontWeight={800}
        flexShrink={0}
        aria-hidden="true"
      >
        {extension}
      </Box>
      <Box flex="1" minW={0}>
        <Text fontSize="15px" fontWeight={700} noOfLines={1} lineHeight="20px">
          {fileName}
        </Text>
        {progress ? (
          <Box pt="6px">
            <ProgressBar
              done={progress.loaded}
              total={progress.total || progress.loaded || 1}
              label={t('chat.file.downloading', { name: fileName })}
            />
            <Text fontSize="13px" color="muted" lineHeight="18px" pt="6px">
              {progress.total
                ? t('chat.file.progress', {
                    done: formatBytes(progress.loaded),
                    total: formatBytes(progress.total),
                  })
                : formatBytes(progress.loaded) || t('chat.file.starting')}
            </Text>
          </Box>
        ) : (
          <Text fontSize="13px" color="muted" lineHeight="18px">
            {[formatBytes(fileSize), extension].filter(Boolean).join(' · ')}
          </Text>
        )}
      </Box>
      {progress ? (
        <IconButton
          aria-label={t('chat.file.cancel')}
          icon={<X size={20} />}
          colorScheme="gray"
          bg="surface.subtle"
          flexShrink={0}
          onClick={() => abort.current?.abort()}
        />
      ) : (
        <IconButton
          aria-label={t('chat.file.download', { name: fileName })}
          icon={<Download size={20} />}
          flexShrink={0}
          onClick={() => void download()}
        />
      )}
    </HStack>
  );
}
